import 'leaflet/dist/leaflet.css';

import React from 'react';
import { Icon as Iconify } from '@iconify/react';
import L from 'leaflet';
import { Marker, TileLayer, MapContainer } from 'react-leaflet';

import { Box, Card, CardContent, Link, Stack, Typography } from '@mui/material';

import { iconIsUrl } from '../../render-icon';

import type { GenericComponent } from '../../types';

type Item<T extends GenericComponent['type']> = Extract<GenericComponent, { type: T }>;

// ---------------------------------------------------------------------------
// image_item — flat image
// ---------------------------------------------------------------------------

export function ImageItemRenderer({ component }: { component: Item<'image_item'> }) {
  return (
    <Stack spacing={0.5}>
      <Box
        component="img"
        src={component.url}
        alt={component.caption ?? ''}
        sx={(theme) => ({
          width: '100%',
          maxHeight: 240,
          objectFit: 'cover',
          borderRadius: `${Number(theme.shape.borderRadius) * 1.5}px`,
        })}
      />
      {component.caption && (
        <Typography variant="caption" color="text.secondary">
          {component.caption}
        </Typography>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// carousel — horizontal scroll-snap, flat
// ---------------------------------------------------------------------------

export function CarouselRenderer({ component }: { component: Item<'carousel'> }) {
  return (
    <Stack spacing={1}>
      {component.title && (
        <Typography variant="subtitle2" color="text.primary">
          {component.title}
        </Typography>
      )}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          pb: 0.5,
          '&::-webkit-scrollbar': { height: 4 },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: 'action.disabled',
            borderRadius: 1,
          },
        }}
      >
        {component.images.map((img, index) => (
          <Stack key={index} sx={{ scrollSnapAlign: 'start', flexShrink: 0 }} spacing={0.5}>
            <Box
              component="img"
              src={img.url}
              alt={img.caption ?? ''}
              sx={(theme) => ({
                width: 260,
                height: 180,
                objectFit: 'cover',
                borderRadius: `${Number(theme.shape.borderRadius) * 1.5}px`,
              })}
            />
            {img.caption && (
              <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 260 }}>
                {img.caption}
              </Typography>
            )}
          </Stack>
        ))}
      </Box>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// youtube — Card (iframe needs it)
// ---------------------------------------------------------------------------

export function YouTubeRenderer({ component }: { component: Item<'youtube'> }) {
  return (
    <Card sx={{ overflow: 'hidden' }}>
      <Box
        component="iframe"
        src={`https://www.youtube.com/embed/${component.videoId}`}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        sx={{ width: '100%', aspectRatio: '16 / 9', border: 0, display: 'block' }}
      />
      {component.title && (
        <CardContent sx={{ '&:last-child': { pb: 2 } }}>
          <Typography variant="subtitle2" color="text.primary">
            {component.title}
          </Typography>
        </CardContent>
      )}
    </Card>
  );
}

const redIcon = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

// ---------------------------------------------------------------------------
// map_location — Leaflet with optional multiple markers
// ---------------------------------------------------------------------------

export function MapLocationRenderer({ component }: { component: Item<'map_location'> }) {
  const allMarkers = component.markers?.length
    ? component.markers
    : [{ latitude: component.latitude, longitude: component.longitude, label: component.label }];

  const lats = allMarkers.map((m) => m.latitude);
  const lngs = allMarkers.map((m) => m.longitude);
  const centerLat = lats.reduce((a, b) => a + b, 0) / lats.length;
  const centerLng = lngs.reduce((a, b) => a + b, 0) / lngs.length;
  const zoom = component.zoom ?? (allMarkers.length === 1 ? 14 : 4);

  const bounds =
    allMarkers.length > 1
      ? L.latLngBounds(allMarkers.map((m) => [m.latitude, m.longitude] as L.LatLngTuple))
      : undefined;

  return (
    <Stack spacing={1}>
      <Box
        sx={(theme) => ({
          borderRadius: `${Number(theme.shape.borderRadius) * 1.5}px`,
          overflow: 'hidden',
          height: 220,
          '& .leaflet-container': { height: '100%', width: '100%' },
        })}
      >
        <MapContainer
          center={[centerLat, centerLng]}
          zoom={zoom}
          bounds={bounds?.pad(0.3)}
          scrollWheelZoom
          zoomControl
          attributionControl={false}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" />
          {allMarkers.map((marker, i) => (
            <Marker key={i} position={[marker.latitude, marker.longitude]} icon={redIcon} />
          ))}
        </MapContainer>
      </Box>
      {(component.label || component.address) && (
        <Stack spacing={0.25}>
          {component.label && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box component={Iconify} icon="mdi:map-marker" width={18} sx={{ flexShrink: 0, color: 'error.main' }} />
              <Typography variant="subtitle2" color="text.primary">
                {component.label}
              </Typography>
            </Stack>
          )}
          {component.address && (
            <Typography variant="body2" color="text.secondary" sx={{ pl: 3.25 }}>
              {component.address}
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// route — Leaflet with origin + destination markers
// ---------------------------------------------------------------------------

export function RouteRenderer({ component }: { component: Item<'route'> }) {
  const bounds = L.latLngBounds(
    [component.origin.latitude, component.origin.longitude],
    [component.destination.latitude, component.destination.longitude]
  );
  const center = bounds.getCenter();
  const directionsUrl = `https://www.google.com/maps/dir/?api=1&origin=${component.origin.latitude},${component.origin.longitude}&destination=${component.destination.latitude},${component.destination.longitude}`;

  return (
    <Stack spacing={1}>
      <Box
        sx={(theme) => ({
          borderRadius: `${Number(theme.shape.borderRadius) * 1.5}px`,
          overflow: 'hidden',
          height: 200,
          '& .leaflet-container': { height: '100%', width: '100%' },
        })}
      >
        <MapContainer
          center={[center.lat, center.lng]}
          zoom={10}
          bounds={bounds.pad(0.3)}
          scrollWheelZoom
          zoomControl
          attributionControl={false}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" />
          <Marker position={[component.origin.latitude, component.origin.longitude]} icon={redIcon} />
          <Marker position={[component.destination.latitude, component.destination.longitude]} icon={redIcon} />
        </MapContainer>
      </Box>
      <Stack spacing={0.25}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            component={Iconify}
            icon="mdi:map-marker-path"
            width={20}
            sx={{ color: 'primary.main', flexShrink: 0 }}
          />
          <Typography variant="subtitle2" noWrap color="text.primary">
            {component.origin.label} → {component.destination.label}
          </Typography>
        </Stack>
        {(component.distance || component.duration) && (
          <Typography variant="body2" color="text.secondary" sx={{ pl: 3.5 }}>
            {[component.distance, component.duration].filter(Boolean).join(' · ')}
          </Typography>
        )}
        <Link
          href={directionsUrl}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          variant="body2"
          color="primary"
          sx={{ pl: 3.5 }}
        >
          Open in Google Maps
        </Link>
      </Stack>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// weather — container.medium surface
// ---------------------------------------------------------------------------

/**
 * Maps a plain-English weather condition (what the LLM tends to emit for
 * `weather.icon` when the field isn't prompt-documented as iconify) to an
 * mdi weather icon. Keyword match is substring-based, order matters —
 * "partly cloudy" must come before "cloudy".
 */
function resolveWeatherIcon(icon: string | null | undefined): string {
  if (!icon) return 'mdi:weather-partly-cloudy';
  if (icon.includes(':') || iconIsUrl(icon)) return icon;

  const lower = icon.toLowerCase().trim();
  const rules: Array<[string[], string]> = [
    [['partly cloudy', 'partly_cloudy', 'partly-cloudy'], 'mdi:weather-partly-cloudy'],
    [['thunder', 'storm', 'lightning'], 'mdi:weather-lightning'],
    [['snow'], 'mdi:weather-snowy'],
    [['hail'], 'mdi:weather-hail'],
    [['rain', 'drizzle', 'shower'], 'mdi:weather-rainy'],
    [['fog', 'mist', 'haze'], 'mdi:weather-fog'],
    [['wind'], 'mdi:weather-windy'],
    [['cloud', 'overcast'], 'mdi:weather-cloudy'],
    [['sun', 'clear'], 'mdi:weather-sunny'],
    [['night'], 'mdi:weather-night'],
  ];
  for (const [keys, resolved] of rules) {
    if (keys.some((k) => lower.includes(k))) return resolved;
  }
  return `mdi:${lower.replace(/\s+/g, '-')}`;
}

export function WeatherRenderer({ component }: { component: Item<'weather'> }) {
  const weatherIcon = resolveWeatherIcon(component.icon);

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2}
      sx={(theme) => ({
        px: 2,
        py: 1.5,
        borderRadius: `${Number(theme.shape.borderRadius) * 1.5}px`,
        bgcolor: theme.palette.container?.medium ?? theme.palette.grey[800],
      })}
    >
      <Box component={Iconify} icon={weatherIcon} width={44} sx={{ flexShrink: 0, color: 'warning.main' }} />
      <Stack flex={1} minWidth={0}>
        <Typography variant="h4" color="text.primary" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
          {component.temperature}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {component.condition}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {component.location}
        </Typography>
      </Stack>
      {(component.humidity || component.wind) && (
        <Stack spacing={0.5} sx={{ flexShrink: 0 }}>
          {component.humidity && (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Box component={Iconify} icon="mdi:water-percent" width={14} sx={{ color: 'info.main' }} />
              <Typography variant="caption" color="text.secondary">
                {component.humidity}
              </Typography>
            </Stack>
          )}
          {component.wind && (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Box component={Iconify} icon="mdi:weather-windy" width={14} sx={{ color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {component.wind}
              </Typography>
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
}
